// @flow
import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import ReactInputPosition, {
  MOUSE_ACTIVATION,
  TOUCH_ACTIVATION
} from 'react-input-position';
import classnames from 'classnames';

import ProgressiveImage from '../../ProgressiveImage';
import WatchClickOutside from '@components/WatchClickOutside';

import FavoriteIcon from '@components/FavoriteIcon';

import { ImageContext } from '@contexts/image';
import { VideoContext } from '@contexts/video';
import { SettingStateContext } from '@contexts/setting';

import Icon from '@components/Icon';

import css from '@styles/productDetail/mainImage';
import magnifyingGlass from '@src/svgIcons/magnifyingGlass.json';

import type { ObjectType } from '@types/misc';

type TProps = {
  style?: ?ObjectType,
  onClick: () => void,
  isFavorited: boolean,
  onToggleFavorite: () => void,
  allowFavorite: boolean
};

function TrackableImageRenderer(props) {
  const {
    itemRef,
    activePosition,
    elementDimensions,
    itemDimensions,
    image
  } = props;

  const mouseXPercent = activePosition.x / elementDimensions.width;
  const mouseYPercent = activePosition.y / elementDimensions.height;

  const rightPosition = elementDimensions.width - itemDimensions.width;
  const bottomPosition = elementDimensions.height - itemDimensions.height;

  const left = mouseXPercent * rightPosition;
  const top = mouseYPercent * bottomPosition;
  const loadedStyles = { width: '100%' };
  const styles = {};

  if (!Number.isNaN(left) && !Number.isNaN(top)) {
    styles.left = left;
    styles.top = top;
  }

  // If image is undefined, we will have a bunch of `undefined` sources defined.
  // This happens when ImageContext first mounts, because teh default value is
  // {}.
  const images = [
    { src: image.thumbnail, styles: loadedStyles, alt: 'Main image' },
    { src: image.standard, styles: loadedStyles, alt: 'Main image' },
    { src: image.large, styles: loadedStyles, alt: 'Main image' }
  ].filter(image => image.src);

  return (
    <ProgressiveImage images={images} imageRef={itemRef} styles={styles} />
  );
}

export default function MainImage({
  style,
  onClick,
  isFavorited,
  onToggleFavorite,
  allowFavorite
}: TProps) {
  const [zoom, setZoom] = useState(false);
  const [playerExpansion, setPlayerExpansion] = useState(false);
  const { image, setImage } = useContext(ImageContext);
  const { video } = useContext(VideoContext);
  const { setting } = useContext(SettingStateContext);
  const videoRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!setting) return;

    const { images } = setting;

    const displayableImages = (images || []).filter(image => image.display);

    if (!(displayableImages && displayableImages.length)) return;

    setImage(displayableImages[0]);
  }, [setImage, setting]);

  useEffect(() => {
    if (!videoRef.current) return;

    videoRef.current[video.state]();
  }, [video.state]);

  // This function makes sure that we don't zoom larger then the screen width
  const handleWindowResize = useCallback(() => {
    if (!document) return;
    if (!document.documentElement) return;
    if (!window) return;
    if (!containerRef.current) return;

    if (zoom && video.url) {
      const container = containerRef.current;
      const containerParent = container.parentElement;
      const docWidth = document.documentElement.clientWidth;

      let containerParentWidth = containerParent.clientWidth; // width with padding

      var computedStyle = window.getComputedStyle(containerParent);

      containerParentWidth -=
        parseFloat(computedStyle.paddingLeft) +
        parseFloat(computedStyle.paddingRight);

      // Remove an extra 40px to allow for 20px on each size
      setPlayerExpansion(
        Math.max(-100, -((docWidth - containerParentWidth - 40) / 2))
      );
    } else {
      if (playerExpansion) setPlayerExpansion(null);
    }
  }, [zoom, video.url, containerRef.current]);

  useLayoutEffect(() => {
    if (!window || !zoom) return;

    window.addEventListener('resize', handleWindowResize);
    handleWindowResize();

    return () => window.removeEventListener('resize', handleWindowResize);
  }, [handleWindowResize]);

  const handleZoomClick = useCallback(
    e => {
      e.stopPropagation();
      setZoom(!zoom);
    },
    [zoom]
  );

  const startZoom = useCallback(() => {
    setZoom(true);
  }, []);

  const endZoom = useCallback(() => {
    setZoom(false);
  }, []);

  if (zoom && video.url && playerExpansion) {
    style = {
      ...style,
      margin: playerExpansion
    };
  }

  if (video.url) {
    return (
      <WatchClickOutside
        className={classnames(css.container, 'video', { zoom })}
        style={style}
        onClick={onClick}
        elRef={containerRef}
        onClickOutside={endZoom}
        enabled={zoom}
      >
        <div className={css.squareContainer}>
          <div className={css.imageContainer}>
            <video autoPlay loop ref={videoRef}>
              <source src={video.url} type='video/mp4' />
              <p>
                <a href={video.url}>video link</a>
              </p>
            </video>
            <div className={css.zoomButton} onClick={handleZoomClick}>
              <Icon svgPaths={magnifyingGlass} />
            </div>
          </div>
        </div>
      </WatchClickOutside>
    );
  } else {
    return (
      <div
        className={classnames(css.container, { zoom })}
        style={style}
        onMouseEnter={startZoom}
        onMouseLeave={endZoom}
        onTouchStart={startZoom}
        onTouchEnd={endZoom}
      >
        <div className={css.squareContainer}>
          <ReactInputPosition
            mouseActivationMethod={MOUSE_ACTIVATION.HOVER}
            touchActivationMethod={TOUCH_ACTIVATION.TOUCH}
            cursorStyleActive={zoom ? 'move' : 'default'}
            className={css.imageContainer}
          >
            <TrackableImageRenderer image={image} />
          </ReactInputPosition>
        </div>

        {allowFavorite ? (
          <FavoriteIcon
            className={css.favoriteLink}
            selected={isFavorited}
            onClick={onToggleFavorite}
          />
        ) : null}
      </div>
    );
  }
}

MainImage.defaultProps = {
  allowFavorite: true
};
